import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { Mat3ButtonComponent } from '../../base-components/mat3-button/mat3-button.component';

import { TranslateModule } from '@ngx-translate/core';

import { FarmStore } from '../../../store/farm.store';
import { RouterFacade } from '../../../store/router.facade';
import { IconComponent } from '../../base-components/icon/icon.component';
import { LottiePlayerComponent } from '../../lottie-player/lottie-player.component';
import { AdaptivePopoverComponent, PopoverContentDirective } from '../adaptive-popover/adaptive-popover.component';
import { AviatorActionPanelComponent, QuickAction } from '../aviator-action-panel/aviator-action-panel.component';
import { ENTITY_COLORS } from '../../../config/entity-colors.config';

@Component({
  selector: 'app-onboarding-modal',
  imports: [
    TranslateModule,
    Mat3ButtonComponent,
    IconComponent,
    LottiePlayerComponent,
    AviatorActionPanelComponent,
    AdaptivePopoverComponent,
    PopoverContentDirective,
  ],
  templateUrl: './onboarding-modal.component.html',
  styleUrls: ['./onboarding-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingModalComponent {
  private routerFacade = inject(RouterFacade);
  protected farmStore = inject(FarmStore);

  public isOpen = input.required<boolean>();
  public closeModal = output<void>();

  protected showInteractionModal = signal<boolean>(false);
  protected isStep1ActionPanelOpen = signal<boolean>(false);
  protected isStep2ActionPanelOpen = signal<boolean>(false);
  protected isCropsActionPanelOpen = signal<boolean>(false);
  protected isEquipmentActionPanelOpen = signal<boolean>(false);
  protected isTasksActionPanelOpen = signal<boolean>(false);
  protected isBuildingsActionPanelOpen = signal<boolean>(false);

  protected onClose(): void {
    this.closeModal.emit();
  }

  protected onBackdropClick(): void {
    this.closeModal.emit();
  }

  protected onRobotClick(): void {
    this.toggleInteractionModal();
  }

  protected toggleInteractionModal(): void {
    this.showInteractionModal.update(val => !val);
  }

  protected closeInteractionModal(): void {
    this.showInteractionModal.set(false);
  }

  protected onTryAssistant(): void {
    this.closeInteractionModal();
    this.closeModal.emit();
  }

  protected readonly step1Actions: QuickAction[] = [
    {
      id: 'manual',
      label: 'onboarding.step1QuickActionManualLabel',
      description: 'onboarding.step1QuickActionManualDescription',
      icon: 'solar:add-square-bold-duotone',
      iconColor: ENTITY_COLORS.fields,
      onClick: () => {
        this.routerFacade.navigate(['/app/fields/create']);
        this.closeStep1ActionPanel();
        this.closeModal.emit();
      }
    }
  ];

  protected readonly step2Actions: QuickAction[] = [
    {
      id: 'manual',
      label: 'onboarding.step2QuickActionManualLabel',
      description: 'onboarding.step2QuickActionManualDescription',
      icon: 'solar:add-square-bold-duotone',
      iconColor: ENTITY_COLORS.livestock,
      onClick: () => {
        this.routerFacade.navigate(['/app/livestock/create']);
        this.closeStep2ActionPanel();
        this.closeModal.emit();
      }
    }
  ];

  protected readonly cropsActions: QuickAction[] = [
    {
      id: 'manual',
      label: 'onboarding.cropsQuickActionManualLabel',
      description: 'onboarding.cropsQuickActionManualDescription',
      icon: 'solar:add-square-bold-duotone',
      iconColor: ENTITY_COLORS.crops,
      onClick: () => {
        this.routerFacade.navigate(['/app/crops/add']);
        this.closeCropsActionPanel();
        this.closeModal.emit();
      }
    }
  ];

  protected readonly equipmentActions: QuickAction[] = [
    {
      id: 'manual',
      label: 'onboarding.equipmentQuickActionManualLabel',
      description: 'onboarding.equipmentQuickActionManualDescription',
      icon: 'solar:add-square-bold-duotone',
      iconColor: ENTITY_COLORS.equipment,
      onClick: () => {
        this.routerFacade.navigate(['/app/equipment/create']);
        this.closeEquipmentActionPanel();
        this.closeModal.emit();
      }
    }
  ];

  protected readonly tasksActions: QuickAction[] = [
    {
      id: 'manual',
      label: 'onboarding.tasksQuickActionManualLabel',
      description: 'onboarding.tasksQuickActionManualDescription',
      icon: 'solar:add-square-bold-duotone',
      iconColor: ENTITY_COLORS.tasks,
      onClick: () => {
        this.routerFacade.navigate(['/app/tasks/create']);
        this.closeTasksActionPanel();
        this.closeModal.emit();
      }
    }
  ];

  protected readonly buildingsActions: QuickAction[] = [
    {
      id: 'manual',
      label: 'onboarding.buildingsQuickActionManualLabel',
      description: 'onboarding.buildingsQuickActionManualDescription',
      icon: 'solar:add-square-bold-duotone',
      iconColor: ENTITY_COLORS.buildings,
      onClick: () => {
        this.routerFacade.navigate(['/app/buildings/create']);
        this.closeBuildingsActionPanel();
        this.closeModal.emit();
      }
    }
  ];

  protected onStepClick(step: number): void {
    if (step === 1) {
      this.openStep1ActionPanel();
    } else if (step === 2) {
      this.openStep2ActionPanel();
    }
  }

  protected openStep1ActionPanel(): void {
    this.isStep1ActionPanelOpen.set(true);
  }

  protected closeStep1ActionPanel(): void {
    this.isStep1ActionPanelOpen.set(false);
  }

  protected openStep2ActionPanel(): void {
    this.isStep2ActionPanelOpen.set(true);
  }

  protected closeStep2ActionPanel(): void {
    this.isStep2ActionPanelOpen.set(false);
  }

  protected openCropsActionPanel(): void {
    this.isCropsActionPanelOpen.set(true);
  }

  protected closeCropsActionPanel(): void {
    this.isCropsActionPanelOpen.set(false);
  }

  protected openEquipmentActionPanel(): void {
    this.isEquipmentActionPanelOpen.set(true);
  }

  protected closeEquipmentActionPanel(): void {
    this.isEquipmentActionPanelOpen.set(false);
  }

  protected openTasksActionPanel(): void {
    this.isTasksActionPanelOpen.set(true);
  }

  protected closeTasksActionPanel(): void {
    this.isTasksActionPanelOpen.set(false);
  }

  protected openBuildingsActionPanel(): void {
    this.isBuildingsActionPanelOpen.set(true);
  }

  protected closeBuildingsActionPanel(): void {
    this.isBuildingsActionPanelOpen.set(false);
  }

  protected navigateToLivestock(): void {
    this.closeModal.emit();
    this.routerFacade.navigate(['/app/livestock/create']);
  }

  protected navigateToCrops(): void {
    this.closeModal.emit();
    this.routerFacade.navigate(['/app/crops/add']);
  }

  protected navigateToFields(): void {
    this.closeModal.emit();
    this.routerFacade.navigate(['/app/fields/create']);
  }

  protected navigateToEquipment(): void {
    this.closeModal.emit();
    this.routerFacade.navigate(['/app/equipment/create']);
  }

  protected navigateToTasks(): void {
    this.closeModal.emit();
    this.routerFacade.navigate(['/app/tasks/create']);
  }

  protected navigateToBuildings(): void {
    this.closeModal.emit();
    this.routerFacade.navigate(['/app/buildings/create']);
  }
}
